import Link from "next/link";
import { Trans } from "next-i18next";
import { useEffect, useState } from "react";
import { useHydrated } from "@/hooks/useHydrated";

import { AUTH_API_HOST } from "@/api/apis";
import { useMyCoupons } from "@/api/CouponApi";
import { isUsable, type MyCoupon } from "@/libs/coupon/issued-coupon";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";

/**
 * GNB 프로필 아바타의 쿠폰 알림.
 *
 * 표시 조건은 **"지금 쓸 수 있는 쿠폰을 갖고 있는지"** 하나다(기획 확정). 그래서 쿠폰을 쓰면
 * 서버의 `used` 가 true 가 되어 dot·말풍선이 함께 사라지고, 안 쓰고 만료돼도 `expireAt` 로 사라진다.
 * "봤는지"를 브라우저에 저장하지 않기 때문에 기기·브라우저가 바뀌어도 표시가 일관된다.
 *
 * 여러 장 보유 시 **만료가 가장 이른 쿠폰**을 노출한다(기획 확정 — 짧은 것부터 쓰게 유도).
 * 참고: 체크아웃·장바구니는 **쓸 수 있는 쿠폰을 전부** 적용한다(라인당 1장, `applyIssuedCoupons`).
 * 여기는 "빨리 사라질 것부터 알린다"는 알림 목적이라 한 장만 보여주는 것이고, 의도된 차이다.
 */

const HOUR_MS = 60 * 60 * 1000;
/** 마지막 1시간 — `분+초` 표시라 1초 갱신이 필요하다. */
const FINE_TICK_MS = 1_000;
/** 그 이전 — `일+시간`/`시간+분` 표시라 분 단위면 충분하다. */
const COARSE_TICK_MS = 60_000;

/**
 * 하이드레이션 후에만 시각을 돌려준다(그 전에는 null) — 서버 렌더 시각과 값이 달라
 * hydration 이 깨지는 것을 막는다. 판정은 공용 `useHydrated`(#5192 에서 사본 수렴).
 *
 * ⚠️ "마운트"가 아니라 **하이드레이션**이다. GNB 는 전 페이지에 상주해 SPA 이동으로
 * 재마운트되지 않으므로 여기선 결과가 같지만, 이름을 맞춰 둔다.
 *
 * `deadline`(만료 시각)을 주면 그때까지 주기적으로 갱신한다. **주기는 남은 시간에 따라 달라진다** —
 * 마지막 1시간은 `분+초` 표시라 1초가 필요하지만 그 전에는 분 단위면 충분하다. GNB 는 전 페이지에
 * 있어 48시간 내내 초당 렌더하면 비용이 크다.
 *
 * 주기를 state 로 두지 않고 **다음 타이머를 콜백 안에서 스스로 예약**한다 — effect 본문에서
 * setState 를 하면 `react-hooks/set-state-in-effect` 에 걸리고 연쇄 렌더가 생긴다.
 */
function useNow(deadline?: number | null): number | null {
  const hydrated = useHydrated();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadline == null) return;
    let id: ReturnType<typeof setTimeout>;
    const nextDelay = (at: number) =>
      deadline - at <= HOUR_MS ? FINE_TICK_MS : COARSE_TICK_MS;
    const schedule = (delay: number) => {
      id = setTimeout(() => {
        const at = Date.now();
        setNow(at); // 타이머 콜백 안 — effect 본문이 아니다
        schedule(nextDelay(at));
      }, delay);
    };
    schedule(nextDelay(Date.now()));
    return () => clearTimeout(id);
  }, [deadline]);

  return hydrated ? now : null;
}

/**
 * 지금 쓸 수 있는 쿠폰 목록. `deadline` 을 주면 그 시각까지 주기적으로 다시 계산한다(카운트다운용).
 * 게스트는 `useMyCoupons` 자체가 조회하지 않으므로 항상 빈 배열이다.
 *
 * ⚠️ **`deadline` 을 주지 않으면 `now` 가 마운트 시각에 얼어붙는다** — 타이머가 없으니
 * 갱신되지 않는다. 그러면 페이지를 열어둔 채 쿠폰이 만료돼도 `isUsable` 이 낡은 시각으로
 * 판정해 계속 "쓸 수 있음" 으로 남는다. 화면에 그리는 표면은 [useLiveUsableCoupons] 를 쓸 것.
 */
export function useUsableCoupons(deadline?: number | null): {
  now: number | null;
  usable: MyCoupon[];
} {
  const { data } = useMyCoupons();
  const now = useNow(deadline);

  if (now === null) return { now: null, usable: [] };
  return { now, usable: (data ?? []).filter((c) => isUsable(c, now)) };
}

/**
 * 갱신 주기 계산의 기준이 되는 만료 시각 — **`now` 에 의존하지 않게 데이터에서만 뽑는다.**
 * `now` 로 필터한 결과에서 뽑으면 "주기가 시각에 의존하고 시각이 주기에 의존하는" 순환이 된다.
 */
function soonestDeadline(coupons: MyCoupon[] | undefined): number | null {
  const times = (coupons ?? []).filter((c) => !c.used).map((c) => c.expireAt);
  return times.length > 0 ? Math.min(...times) : null;
}

/**
 * 화면에 그리는 세 표면(dot·꼬리·말풍선)이 쓰는 훅. **만료 시각을 안에서 계산해 넘긴다.**
 *
 * 왜 따로 두는가: `useUsableCoupons()` 를 인자 없이 부르면 `now` 가 마운트 시각에 얼어붙어
 * (타이머가 안 걸린다) 페이지를 열어둔 채 쿠폰이 만료돼도 그 표면이 남는다. 세 표면이 각자
 * 훅을 부르므로 **한 곳만 빠뜨리면 만료 후 일부만 사라진다** — 카운트다운 문구를 없앨 때
 * 실제로 그 상태가 됐다(말풍선은 사라지고 꼬리·dot 만 남는 그림).
 */
function useLiveUsableCoupons(): MyCoupon[] {
  const { data } = useMyCoupons();
  return useUsableCoupons(soonestDeadline(data)).usable;
}

/** 만료가 가장 이른 1장. */
function pickSoonest(usable: MyCoupon[]): MyCoupon {
  return usable.reduce((a, b) => (a.expireAt <= b.expireAt ? a : b));
}

/**
 * 아바타 우상단 dot. 부모에 `relative` 가 있어야 한다.
 *
 * 만료 반영을 "쿠폰 쿼리 갱신(staleTime 30초)에 맡긴다"고 적혀 있었지만 **그건 성립하지 않는다** —
 * 쿼리가 다시 와도 판정 기준 시각(`now`)이 갱신되지 않으면 만료를 못 넘는다. 그래서 다른 두
 * 표면과 같은 [useLiveUsableCoupons] 를 쓴다.
 */
export function ProfileCouponDot() {
  const { t } = useTrans(["gnb"]);
  const usable = useLiveUsableCoupons();

  if (usable.length === 0) return null;

  return (
    <>
      <span
        aria-hidden
        className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-60"
      />
      {/* dot 은 시각 정보뿐이라 스크린리더용 문구를 함께 둔다. */}
      <span className="sr-only">{t("You have a coupon to use.")}</span>
    </>
  );
}

/** 가방(장바구니) 아이콘 크기 = `ShoppingBagIcon` 의 `size-6`. */
const CART_ICON_SIZE_PX = 24;
const CARET_SIZE_PX = 8;
/** 말풍선 상단이 기준 박스(h-full) 세로 중앙에서 얼마나 아래인가 — 아래 `top-[calc(50%+21px)]`. */
const BUBBLE_TOP_FROM_CENTER_PX = 21;
/**
 * 꼬리의 `top` 오프셋(px, 가방 래퍼 하단 기준).
 *
 * 꼬리와 말풍선은 **기준 박스가 다르다**(꼬리=가방 아이콘, 말풍선=GNB 우측 묶음). 그래도 붙어
 * 보여야 하므로 세로를 맞춘다: 두 기준 박스의 세로 중앙이 같으므로
 * `말풍선 상단(중앙+21) − 꼬리 절반` 과 `아이콘 하단(중앙+12)` 의 차이가 이 값이다.
 * 세 입력 모두 상수로 두어 하나만 바뀌어도 테스트가 먼저 깨지게 한다.
 */
export const CARET_TOP_FROM_BAG_PX =
  BUBBLE_TOP_FROM_CENTER_PX - CARET_SIZE_PX / 2 - CART_ICON_SIZE_PX / 2;

/**
 * 말풍선의 꼬리. **가방 아이콘 기준**으로 렌더한다 — 부모에 `relative` 가 있어야 한다.
 *
 * 왜 말풍선 안이 아니라 따로 있는가: 이 알림은 "장바구니에서 쓰라"는 안내라 꼬리가 가방을
 * 가리켜야 하지만(마케팅 요청 2026-08-20), **말풍선 박스 자체를 왼쪽으로 옮기면 안 된다.**
 * 말풍선은 헤더 아래로 돌출하는데 네비 가로 구간과 겹치는 순간 드롭다운으로 내려가는 포인터를
 * 가로채 패널이 닫힌다(`docs/pitfalls.md` #26). 그래서 **말풍선은 제자리, 꼬리만 이동**한다.
 *
 * `left-1/2 -translate-x-1/2` 라 "가방까지의 거리" 상수가 없다 — 그 거리는 조건부 형제
 * (`Tracking`·`LocaleButton`) 때문에 로그인 상태·화면폭마다 달라진다.
 * 장식이라 `aria-hidden` + `pointer-events-none`(포인터를 먹지 않게).
 */
export function CouponTooltipCaret() {
  const usable = useLiveUsableCoupons();

  if (usable.length === 0) return null;

  return (
    <span
      aria-hidden
      data-testid="coupon-tooltip-caret"
      className="pointer-events-none absolute left-1/2 -translate-x-1/2 rotate-45 bg-coolgray-90"
      style={{
        top: `calc(100% + ${CARET_TOP_FROM_BAG_PX}px)`,
        width: CARET_SIZE_PX,
        height: CARET_SIZE_PX,
      }}
    />
  );
}

/**
 * 아바타 아래 한 줄 말풍선. 부모에 `relative` 가 있어야 한다.
 * 쓸 수 있는 쿠폰이 있는 동안 계속 노출한다(기획 확정 — 사용 전까지 계속 알린다).
 *
 * 위치는 **GNB 우측 묶음 우측 끝 고정**이다(#26 — 왼쪽으로 옮기면 드롭다운 통로를 막는다).
 * 가방을 가리키는 일은 [CouponTooltipCaret] 이 맡는다.
 *
 * 문구는 마케팅 확정(2026-08-21): `Don't miss your {{percent}}% OFF!`.
 * **남은 시간(카운트다운)은 표시하지 않는다** — 전달된 시안이 한 줄이고, 문구가 길어질수록
 * 말풍선이 왼쪽으로 자라 #26 통로에 가까워진다. 되살리려면 `tests/gnb-coupon-tooltip.spec.ts`
 * 의 1024 × de·es·fr 침범 가드를 먼저 통과시켜야 한다.
 *
 * 표시하지 않아도 **만료 시각은 넘긴다**([useLiveUsableCoupons]) — 안 넘기면 만료된 쿠폰이
 * 화면에 남는다.
 */
export function CouponTooltip({
  onMouseEnter,
  onMouseLeave,
}: {
  /** 네비 통로 유지용. `useNavCorridorBridge()` 가 준다 — 없으면 dead zone 이 생긴다. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
} = {}) {
  const { t } = useTrans(["gnb"]);
  const usable = useLiveUsableCoupons();

  if (usable.length === 0) return null;

  const soonest = pickSoonest(usable);

  return (
    <Link
      // 쿠폰함 정본은 **계정(auth) 화면**이다(#5127) — 계정관리·구독·개인정보와 같은 자리에
      // 있고 탭·사용 이력·사용 안내까지 들어가 있다. 쇼핑몰 `/coupons` 는 제거됐다.
      // 슬래시를 붙이지 않는 이유는 `next.config.mjs` 의 `/coupons` 리다이렉트 주석 참조.
      href={`${AUTH_API_HOST}/coupons`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // GNB 우측 묶음 기준 우측 정렬 — 헤더 콘텐츠 오른쪽 라인에 맞춘다.
      // 한 줄 고정(whitespace-nowrap). 언어별 문구 길이가 달라도 폭만 늘어난다.
      className={cn(
        // 세로 위치는 **아바타 기준**으로 잡는다. 기준 박스(h-full)의 세로 중앙이 아바타 중앙이므로
        // `50% + 아바타 절반(15px) + 간격(6px)`. 헤더 하단 기준으로 맞추면 헤더 높이가
        // 화면폭마다 달라(64/70px) 간격이 어긋난다.
        // ⚠️ 이 값을 바꾸면 [CARET_TOP_FROM_BAG_PX] 도 함께 바뀌어야 한다(꼬리가 떨어진다).
        "absolute right-0 top-[calc(50%+21px)] z-40 flex w-max items-center gap-1.5",
        "whitespace-nowrap rounded-full bg-coolgray-90 px-3 py-1.5 text-xs text-white shadow-lg",
      )}
    >
      {/* 강조 구간(`<hl>`)을 컴포넌트로 두는 이유: 언어마다 그 구간이 **문장 안 어디로든**
          갈 수 있다(일본어는 문장 맨 앞, 영어는 끝). 앞뒤를 두 개의 키로 쪼개면 어순이
          고정돼 번역이 어색해진다. 색은 기획 확정 — 검정 배경·흰 글씨에 할인율만 노랑. */}
      <Trans
        t={t}
        i18nKey={`Don't miss your <hl>{{percent}}% OFF!</hl>`}
        values={{ percent: soonest.percent }}
        components={{
          hl: <span className="font-600 text-bhaptics-yellow" />,
        }}
      />
    </Link>
  );
}
